import type { PropsWithChildren } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import { connect } from 'react-redux'

import config from 'constants/config'
import { hasIncludedGuestsExceeded } from 'lib/checkout/cartReservationUtils'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import {
  Root,
  CacheOffText,
} from '../styles'
import { decodeSearchParams } from 'lib/search/searchUtils'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import CruisesDiscountTsAndCsModal from 'components/Cruises/CruisesDiscountTsAndCsModal'
import ModalContext from 'contexts/ModalContext'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import useCruiseOfferView from 'hooks/Cruise/useCruiseOfferView'

interface Props {
  className?: string
  durationLabel: string
  flightCacheOff?: boolean
  hasClause?: boolean
  infoBar?: boolean
  infoBarMedium?: boolean
  infoBarMini?: boolean
  isFlightPrice?: boolean
  label: string
  offerSaleUnit: string
  price: number
  roomOccupancy?: string
  shouldShowValue?: boolean
  showFlights?: boolean
  value: number
  originAirport?: App.Airport
  noFlightsFromOrigin?: boolean
  roomType?: App.PackageRoomType
  searchLocation?: any
  roomPolicyDescription?: string
  availablePackage?: App.Package
  offer: App.TourOffer | App.TourOfferSummary
  showDiscountPill?: boolean
  highestDiscountPercent?: number
  isCruise?: boolean
}

function OfferPrice({
  durationLabel,
  hasClause,
  infoBar,
  label,
  offerSaleUnit,
  price,
  roomOccupancy,
  shouldShowValue,
  showFlights,
  flightCacheOff,
  isFlightPrice,
  value,
  children,
  originAirport,
  noFlightsFromOrigin,
  roomType,
  searchLocation,
  roomPolicyDescription,
  availablePackage,
  offer,
  showDiscountPill,
  highestDiscountPercent,
  isCruise,
}: PropsWithChildren<Props>) {
  const intl = useIntl()
  const { lowestPrice } = useCruiseOfferView({ offer })
  const showIncludedGuestsExceeded = useMemo(() => {
    if (!config.SEARCH_ENABLED || !roomType || !availablePackage || !!availablePackage?.roomRate?.extraGuestSurcharges.length) {
      return false
    }

    const filters = decodeSearchParams(searchLocation)
    return filters.rooms.some(room => hasIncludedGuestsExceeded(room, availablePackage, offer))
  }, [roomType, availablePackage, searchLocation, offer])

  const hotelPrice = price

  const showModal = useContext(ModalContext)

  const openDiscountTermsAndConditionsModal = useCallback(() => {
    showModal(<CruisesDiscountTsAndCsModal />)
  }, [showModal])

  const discountPercentage = useMemo(() => {
    if (showDiscountPill && shouldShowValue && value >= price) {
      return getValueOffPercent(value, price)
    }
  }, [showDiscountPill, shouldShowValue, value, price])

  return (
    <Root data-testid={`${isFlightPrice ? 'flights-' : ''}pricing`}>
      {showFlights && <CaptionTextBlock
        variant="large"
        format="titlecase"
        weight="bold"
        startIcon={isFlightPrice && <LinePlaneFlyIcon />}
      >
        {label}
      </CaptionTextBlock>}
      {isFlightPrice && flightCacheOff &&
        <CacheOffText
          className={clsx({ short: !roomOccupancy, infobar: infoBar })}
          variant="medium"
          colour="light-foreground-alt"
        >
          {noFlightsFromOrigin && originAirport &&
            intl.formatMessage(
              {
                id: 'QNJKGy6A11iBS6okv1S7',
                defaultMessage: 'No flights available from {airportName}. Please select another airport',
                description: 'Message when no flights are available from the selected origin airport',
              },
              { airportName: originAirport.name },
            )}
          {(!noFlightsFromOrigin || !originAirport) &&
            intl.formatMessage({
              id: '_igNVaWASz6D-F033xq0',
              defaultMessage: 'Add flights to the hotel package. Select from all major airlines at the next step.',
              description: 'Prompt to add flights to hotel package when flight prices are not cached',
            })}
        </CacheOffText>}
      {(!isFlightPrice || !flightCacheOff) && <>
        {hotelPrice && <>
          {isCruise && offer.lowestPricePackage?.duration && <PriceRowPriceCaption>
            {intl.formatMessage(
              {
                id: 'CqrwM_ZAGOboP6KHgD-t',
                defaultMessage: '{nights, plural, one {# night} other {# nights}} from',
                description: 'Cruise price caption showing number of nights, e.g. "7 nights from"',
              },
              { nights: offer.lowestPricePackage.duration - 1 },
            )}
          </PriceRowPriceCaption>}
          {(!isCruise || !offer.lowestPricePackage?.duration) && <PriceRowPriceCaption>
            {intl.formatMessage(
              {
                id: '141XrMTBaqR-4086B-nW',
                defaultMessage: '{durationLabel} from',
                description: 'Price caption showing duration label followed by "from", e.g. "5 days from"',
              },
              { durationLabel },
            )}
          </PriceRowPriceCaption>}
          <PriceRowPrice
            size="L"
            price={Math.ceil(hotelPrice)}
            saleUnit={`${offerSaleUnit}${hasClause ? '*' : ''}`}
          />
        </>}
        {isCruise && <>
          {shouldShowValue && value >= price &&
            <PriceRowValueDiscountWithCaption
              size="M"
              data-testid="value-box"
              originalValue={Math.ceil(value)}
              discountPercentage={highestDiscountPercent}
              onInfoIconClick={openDiscountTermsAndConditionsModal}
            />}
          {!isFlightPrice && roomOccupancy && !lowestPrice?.secondaryPrice && <PriceRowCaption>
            {intl.formatMessage({
              id: 'zPk7u6kb0uATkrdlXGhx',
              defaultMessage: 'Twin Share',
              description: 'Caption indicating cruise price is for twin share occupancy',
            })}
          </PriceRowCaption>}
          {isFlightPrice && <PriceRowCaption>
            {intl.formatMessage({
              id: 'D0OUM_4KxdkmC5__JbXT',
              defaultMessage: 'Two Adults',
              description: 'Caption indicating flight price is for two adults in the price section',
            })}
          </PriceRowCaption>}
        </>}
        <PriceRowTaxesAndFees />
        {!isCruise && !isFlightPrice && roomOccupancy && <PriceRowCaption>{roomOccupancy}</PriceRowCaption>}
        {!isCruise && isFlightPrice && <PriceRowCaption>
          {intl.formatMessage({
            id: 'D0OUM_4KxdkmC5__JbXT',
            defaultMessage: 'Two Adults',
            description: 'Caption indicating flight price is for two adults in the price section',
          })}
        </PriceRowCaption>}
      </>}
      {children}
      {showIncludedGuestsExceeded && !(isFlightPrice && flightCacheOff) && (
        <AdditionalGuestsPopup
          complex={false}
          description={intl.formatMessage({
            id: 'rbH7Nd8Dhji4-oIjb3D8',
            defaultMessage: '+ Extra guest surcharge',
            description: 'Description for extra guest surcharge in the price section',
          })}
          modalContent={roomPolicyDescription}
        />
      )}
      {!isCruise && shouldShowValue && value >= price &&
        <PriceRowValueDiscountWithCaption
          size="M"
          data-testid="value-box"
          originalValue={Math.ceil(value)}
          discountPercentage={discountPercentage}
          onInfoIconClick={openDiscountTermsAndConditionsModal}
        />}
    </Root>
  )
}

function mapStateToProps(state: App.State) {
  return {
    searchLocation: state.router.location.search,
  }
}

export default connect(mapStateToProps)(OfferPrice)
